import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { ShieldAlert, RefreshCw, AlertTriangle, Users, Link2 } from "lucide-react";
import {
  adminSuspiciousOverview,
  type SuspiciousOverviewPayload,
} from "@/lib/antifraud/antifraud.functions";

export const Route = createFileRoute("/admin/anti-fraud")({
  component: AntiFraudPage,
  head: () => ({ meta: [{ title: "Anti-Fraud · VerseSport" }] }),
  errorComponent: ({ error }) => (
    <div className="p-6 text-sm text-destructive">
      No se pudo cargar el panel: {error.message}
    </div>
  ),
  notFoundComponent: () => <div className="p-6 text-sm">No encontrado.</div>,
});

type Row = Record<string, unknown>;

function AntiFraudPage() {
  const fn = useServerFn(adminSuspiciousOverview);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin-anti-fraud"],
    queryFn: () => fn(),
    refetchInterval: 30_000,
  });

  const payload = useMemo<SuspiciousOverviewPayload | null>(() => {
    if (!data || !data.ok || !data.payload) return null;
    try { return JSON.parse(data.payload) as SuspiciousOverviewPayload; } catch { return null; }
  }, [data]);

  return (
    <main className="mx-auto max-w-6xl space-y-6 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-black tracking-tight">
            <ShieldAlert className="h-6 w-6 text-amber-500" />
            Anti-Fraud · PR-34B
          </h1>
          <p className="text-sm text-muted-foreground">
            Vista read-only: dispositivos, referidos y claims XP sospechosos. Requiere superadmin.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-bold hover:bg-muted disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
          Refrescar
        </button>
      </header>

      {isLoading && <p className="text-sm text-muted-foreground">Cargando…</p>}
      {error && <p className="text-sm text-destructive">Error: {(error as Error).message}</p>}
      {data && !data.ok && (
        <p className="text-sm text-destructive">No autorizado: {data.error}</p>
      )}

      {payload && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <SuspiciousDevicesPanel rows={payload.suspicious_devices as Row[]} />
          <SuspiciousReferralsPanel rows={payload.suspicious_referrals as Row[]} />
          <SuspiciousClaimsPanel rows={payload.suspicious_xp_claims as Row[]} />
          <AuditTrailPanel rows={payload.recent_audit as Row[]} />
        </div>
      )}
    </main>
  );
}

function Card({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-background/60 p-4">
      <header className="mb-3 flex items-center gap-2 text-sm font-black">
        {icon}
        {title}
      </header>
      {children}
    </section>
  );
}

function band(score: number): string {
  if (score >= 60) return "HIGH";
  if (score >= 30) return "MEDIUM";
  return "LOW";
}
function bandColor(b: string) {
  if (b === "HIGH") return "bg-red-500/15 text-red-500 border-red-500/40";
  if (b === "MEDIUM") return "bg-amber-500/15 text-amber-500 border-amber-500/40";
  return "bg-emerald-500/15 text-emerald-500 border-emerald-500/40";
}

function SuspiciousDevicesPanel({ rows }: { rows: Row[] }) {
  return (
    <Card title={`Dispositivos sospechosos (${rows.length})`} icon={<Users className="h-4 w-4 text-amber-500" />}>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">Sin señales en los últimos 14 días.</p>
      ) : (
        <ul className="space-y-1.5 text-[11px]">
          {rows.slice(0, 15).map((r, i) => {
            const score = Number(r.score ?? 0);
            const b = String(r.band ?? band(score));
            const flags = Array.isArray(r.flags) ? (r.flags as string[]) : [];
            return (
              <li key={i} className="flex flex-col gap-1 rounded border border-border bg-background/40 p-2">
                <div className="flex items-center justify-between gap-2">
                  <code className="truncate text-[10px] opacity-70">{String(r.device_hash).slice(0, 16)}…</code>
                  <span className={`rounded border px-1.5 py-0.5 font-black ${bandColor(b)}`}>{b} · {score}</span>
                </div>
                <div className="text-muted-foreground">
                  users={String(r.users ?? 0)} · signups24h={String(r.signups_24h ?? 0)} · claims24h={String(r.claims_24h ?? 0)} · refs7d={String(r.referrals_7d ?? 0)}
                </div>
                {flags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {flags.map((f) => (
                      <span key={f} className="rounded bg-muted px-1.5 py-0.5 text-[10px]">{f}</span>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function SuspiciousReferralsPanel({ rows }: { rows: Row[] }) {
  return (
    <Card title={`Referidos sospechosos (${rows.length})`} icon={<Link2 className="h-4 w-4 text-amber-500" />}>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">Sin clusters de referidos.</p>
      ) : (
        <ul className="space-y-1 text-[11px]">
          {rows.slice(0, 15).map((r, i) => (
            <li key={i} className="flex items-center justify-between rounded border border-border bg-background/40 p-2">
              <code className="text-[10px]">{String(r.referral_code)}</code>
              <span className="text-muted-foreground">attempts={String(r.count)} · rejected={String(r.rejected)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function SuspiciousClaimsPanel({ rows }: { rows: Row[] }) {
  return (
    <Card title={`Claims XP sospechosos (${rows.length})`} icon={<AlertTriangle className="h-4 w-4 text-amber-500" />}>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">Sin clusters de claims XP en 24h.</p>
      ) : (
        <ul className="space-y-1 text-[11px]">
          {rows.slice(0, 15).map((r, i) => (
            <li key={i} className="flex items-center justify-between rounded border border-border bg-background/40 p-2">
              <span>
                <code className="text-[10px] opacity-70">{String(r.device_hash).slice(0, 16)}…</code>
                <span className="ml-2 font-bold">{String(r.reason)}</span>
              </span>
              <span className="text-muted-foreground">claims24h={String(r.claims_24h)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function AuditTrailPanel({ rows }: { rows: Row[] }) {
  return (
    <Card title={`Audit trail (${rows.length})`} icon={<ShieldAlert className="h-4 w-4 text-amber-500" />}>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">Sin eventos recientes.</p>
      ) : (
        <ul className="space-y-1 text-[11px]">
          {rows.slice(0, 25).map((r, i) => {
            const score = r.risk_score == null ? null : Number(r.risk_score);
            const flags = Array.isArray(r.fraud_flags) ? (r.fraud_flags as string[]) : [];
            return (
              <li key={i} className="flex flex-col gap-1 rounded border border-border bg-background/40 p-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-bold">{String(r.kind)} · {String(r.reward_type ?? "")}</span>
                  {score != null && (
                    <span className={`rounded border px-1.5 py-0.5 font-black ${bandColor(band(score))}`}>{score}</span>
                  )}
                </div>
                <div className="text-muted-foreground">
                  {String(r.created_at)} {r.device_hash ? ` · ${String(r.device_hash).slice(0, 12)}…` : ""}
                </div>
                {flags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {flags.map((f) => (
                      <span key={f} className="rounded bg-muted px-1.5 py-0.5 text-[10px]">{f}</span>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}